import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';

import {
  DISPLAY,
  FLEX,
  FLEX_JUSTIFY,
  BORDER_WIDTH,
  FONT_WEIGHTS,
  TYPE_SCALE,
  COLOR_SCALE,
  PADDING_X,
  PADDING_Y,
  SHADOW_SCALE,
  RADIUS_SCALE,
  INTERACTIVE_STATES,
} from './tokens';

const BADGE_BASE_CLASSES = cn(
  DISPLAY.inlineFlex,
  FLEX.flexItemsCenter,
  FLEX_JUSTIFY.center,
  BORDER_WIDTH.thin,
  FONT_WEIGHTS.semibold,
  'leading-tight',
  'tracking-tight',
  TYPE_SCALE['2xs'],
  `sm:${TYPE_SCALE.xs}`,
  INTERACTIVE_STATES.transitions.colors,
  'focus-visible:outline-none',
  'focus-visible:ring-2',
  'focus-visible:ring-primary/40',
  'focus-visible:ring-offset-2',
  'focus-visible:ring-offset-background'
);

const sizeVariants = {
  sm: cn(PADDING_X['2'], PADDING_Y['0.5'], TYPE_SCALE['3xs'], `sm:${TYPE_SCALE['2xs']}`),
  md: cn(PADDING_X['2.5'], PADDING_Y['0.5'], TYPE_SCALE['2xs'], `sm:${TYPE_SCALE.xs}`),
  lg: cn(PADDING_X['3'], PADDING_Y['1'], TYPE_SCALE.xs, `sm:${TYPE_SCALE.sm}`),
};

const radiusVariants = {
  sm: `${RADIUS_SCALE.sm}`,
  md: `${RADIUS_SCALE.md}`,
  lg: `${RADIUS_SCALE.lg}`,
  xl: `${RADIUS_SCALE.xl}`,
  pill: RADIUS_SCALE.pill,
  full: RADIUS_SCALE.full,
};

const BADGE_VARIANT_STYLES = {
  primary: COLOR_SCALE['badge-primary'],
  secondary: COLOR_SCALE['badge-secondary'],
  muted: COLOR_SCALE['badge-muted'],
  ghost: COLOR_SCALE['badge-ghost'],
  outline: COLOR_SCALE['badge-outline'],
  info: COLOR_SCALE['badge-info'],
  success: `${COLOR_SCALE['badge-success']} animate-[badge-bounce_0.4s_ease] will-change-transform [--badge-bounce:_scale(0.96)_scale(1.04)_scale(1)]`,
  warning: COLOR_SCALE['badge-warning'],
  danger: COLOR_SCALE['badge-danger'],
  destructive: `${COLOR_SCALE['badge-destructive']} animate-[badge-shake_0.3s_ease] will-change-transform`,
  elevated: `${COLOR_SCALE['badge-elevated']} ${SHADOW_SCALE.sm} shadow-slate-200/80`,
  default: COLOR_SCALE['badge-primary'],
};

const BADGE_VARIANT_ALIASES = {
  default: 'primary',
  destructive: 'danger',
};

export const badgeVariants = cva(BADGE_BASE_CLASSES, {
  variants: {
    variant: {
      ...BADGE_VARIANT_STYLES,
      ...Object.fromEntries(
        Object.entries(BADGE_VARIANT_ALIASES).map(([alias, target]) => [
          alias,
          BADGE_VARIANT_STYLES[target as keyof typeof BADGE_VARIANT_STYLES],
        ])
      ),
    },
    size: sizeVariants,
    radius: radiusVariants,
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
    radius: 'pill',
  },
});
